'use client';
import { formatPhoneNumber } from '@utils/formatPhoneNumber';
import { Order } from '@repo/ui/src/types/order';
import OrderDetailPaymentInfo from './OrderDetailPaymentInfo';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';

export interface OrderDetailSummarySectionProps {
  orderDetails: Order['details'];
}

const OrderDetailCustomerSummary = ({
  orderDetails: { shipments, customerInfo, paymentInstruments, billingAddress },
}: OrderDetailSummarySectionProps) => {
  const { t } = useTranslation();
  const { shippingMethod, shippingAddress } = shipments?.[0] ?? {};
  return (
    <div>
      <h5 className="tra-text-web-heading-5 tw-mb-5">
        {t('orderSummary.customerInformationLabel', undefined, {
          default: 'Customer Information',
        })}
      </h5>
      <div className="tw-grid sm:tw-grid-flow-col sm:tw-grid-rows-[repeat(3,auto)] sm:tw-grid-cols-2 sm:tw-justify-start tw-gap-6 tw-p-6 tw-bg-warm-100 tw-border tw-border-warm-300">
        <div>
          <p className="tra-text-web-body-m tw-font-bold tw-mb-2">
            {t('orderSummary.customerInformationLabel', undefined, {
              default: 'Customer Information',
            })}
          </p>
          {customerInfo?.email && (
            <p className="tra-text-web-body-m tw-mb-0 tw-break-words">{customerInfo.email}</p>
          )}
        </div>
        {shippingAddress && (
          <div>
            <p className="tra-text-web-body-m tw-font-bold tw-mb-2">
              {t('orderSummary.shippingAddressLabel', undefined, {
                default: 'Shipping Address',
              })}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {shippingAddress.fullName.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {shippingAddress.address1.toLowerCase()} {shippingAddress.address2?.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {shippingAddress.city.toLowerCase()}, {shippingAddress.stateCode.toUpperCase()}{' '}
              {shippingAddress.postalCode}
            </p>
            {shippingAddress.phone && (
              <p className="tra-text-web-body-m tw-mb-0">
                {formatPhoneNumber(shippingAddress.phone)}
              </p>
            )}
          </div>
        )}
        {shippingMethod && (
          <div>
            <p className="tra-text-web-body-m tw-font-bold tw-mb-2">
              {t('orderSummary.shippingMethodLabel', undefined, {
                default: 'Shipping Method',
              })}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {shippingMethod.name.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {shippingMethod.description.toLowerCase()}
            </p>
          </div>
        )}
        <OrderDetailPaymentInfo paymentInstruments={paymentInstruments} />
        {billingAddress && (
          <div>
            <p className="tra-text-web-body-m tw-font-bold tw-mb-2">
              {t('orderSummary.billingAddressLabel', undefined, {
                default: 'Billing Address',
              })}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {billingAddress.fullName.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {billingAddress.address1.toLowerCase()} {billingAddress.address2?.toLowerCase()}
            </p>
            <p className="tra-text-web-body-m tw-mb-0 tw-capitalize">
              {billingAddress.city.toLowerCase()}, {billingAddress.stateCode.toUpperCase()}{' '}
              {billingAddress.postalCode}
            </p>
            {billingAddress.phone && (
              <p className="tra-text-web-body-m tw-mb-0">
                {formatPhoneNumber(billingAddress.phone)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailCustomerSummary;
